import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listarClientes } from '../api/clientes';
import { CabecalhoTela } from '../components/Shell';
import {
  BlocoAviso,
  Campo,
  Chip,
  EstadoVazio,
  LinhasEsqueleto,
  PaginacaoBarra,
} from '../components/ui';
import { ApiError } from '../lib/http';

const LIMITE = 25;

function useDebounce<T>(valor: T, ms: number): T {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export function ClientesPagina(): ReactNode {
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const buscaDebounce = useDebounce(busca, 300);
  const campo = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => setPagina(1), [buscaDebounce]);

  useEffect(() => {
    campo.current?.focus();
    const atalho = (e: KeyboardEvent): void => {
      if (e.key === '/' && document.activeElement !== campo.current) {
        e.preventDefault();
        campo.current?.focus();
      }
    };
    window.addEventListener('keydown', atalho);
    return () => window.removeEventListener('keydown', atalho);
  }, []);

  const q = useQuery({
    queryKey: ['clientes', buscaDebounce, pagina],
    queryFn: () => listarClientes({ busca: buscaDebounce || undefined, pagina, limite: LIMITE }),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <CabecalhoTela titulo="Clientes" />
      <Campo
        ref={campo}
        rotulo="Buscar"
        placeholder="Buscar por nome, telefone ou código"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
      />

      <div className="mt-4">
        {q.isLoading && <LinhasEsqueleto colunas={5} />}

        {q.isError && (
          <BlocoAviso tom="aviso" aoTentarDeNovo={() => void q.refetch()}>
            {q.error instanceof ApiError && q.error.ehRede
              ? 'Não foi possível conectar.'
              : 'Não foi possível carregar os clientes.'}
          </BlocoAviso>
        )}

        {q.data && q.data.itens.length === 0 && (
          <EstadoVazio
            titulo={
              buscaDebounce
                ? `Nenhum cliente encontrado para «${buscaDebounce}».`
                : 'Nenhum cliente cadastrado ainda.'
            }
          >
            <Link to="/" className="text-acento-texto underline">
              Ir para Cadastro
            </Link>
          </EstadoVazio>
        )}

        {q.data && q.data.itens.length > 0 && (
          <>
            <table className="w-full border-collapse text-corpo">
              <thead>
                <tr className="border-b border-linha text-left font-display text-rotulo uppercase text-tinta-2">
                  <th className="py-2 font-normal">Nome</th>
                  <th className="py-2 font-normal">Telefone</th>
                  <th className="py-2 font-normal">Código</th>
                  <th className="py-2 font-normal">Cadastrado</th>
                  <th className="py-2 text-right font-normal">Brindes</th>
                </tr>
              </thead>
              <tbody>
                {q.data.itens.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/clientes/${c.id}`)}
                    className="cursor-pointer border-b border-linha hover:bg-superficie"
                  >
                    <td className="py-2.5 font-medium text-tinta-forte">
                      {c.nome}
                      {c.tipoParticipante === 'funcionario' && (
                        <span className="ml-2">
                          <Chip>Funcionário</Chip>
                        </span>
                      )}
                    </td>
                    <td className="tnum py-2.5">{c.telefone}</td>
                    <td className="py-2.5">
                      {c.codigo ? <span className="font-codigo">{c.codigo.codigo}</span> : '—'}
                    </td>
                    <td className="tnum py-2.5">{formatarData(c.criadoEm)}</td>
                    <td className="tnum py-2.5 text-right">
                      {c.vouchersDisponiveis > 0 ? c.vouchersDisponiveis : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginacaoBarra
              pagina={q.data.pagina}
              limite={q.data.limite}
              total={q.data.total}
              aoIr={setPagina}
            />
          </>
        )}
      </div>
    </div>
  );
}

export function formatarData(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}
